// Service for Firebase Storage operations - now uses backend API
import { getIdToken } from '../contexts/AuthContext';
import { buildApiUrl, getAuthHeaders } from '../config/api';

// Cache for profile picture URLs to avoid repeated requests
const profilePictureUrlCache = new Map();

/**
 * Get profile picture download URL via backend API
 * @param {string} profilePicturePath - Storage path to the profile picture
 * @returns {Promise<string|null>} Download URL or null if error
 */
export const getProfilePictureUrl = async (profilePicturePath) => {
  if (!profilePicturePath) {
    return null;
  }

  // Check cache first
  if (profilePictureUrlCache.has(profilePicturePath)) {
    return profilePictureUrlCache.get(profilePicturePath);
  }

  try {
    const idToken = await getIdToken();
    if (!idToken) {
      console.error('Not authenticated - cannot fetch profile picture');
      return null;
    }

    const response = await fetch(buildApiUrl('/users/profile-picture'), {
      method: 'POST',
      headers: getAuthHeaders(idToken),
      body: JSON.stringify({ path: profilePicturePath })
    });

    if (!response.ok) {
      if (response.status === 404) {
        // Cache null for not found
        profilePictureUrlCache.set(profilePicturePath, null);
        return null;
      }
      throw new Error(`Failed to get profile picture: ${response.statusText}`);
    }

    const data = await response.json();
    const url = data.url || null;
    
    // Cache the URL
    profilePictureUrlCache.set(profilePicturePath, url);
    
    return url;
  } catch (error) {
    console.error(`Error getting profile picture URL for ${profilePicturePath}:`, error);
    // Cache null to avoid repeated failed requests
    profilePictureUrlCache.set(profilePicturePath, null);
    return null;
  }
};

/**
 * Clear the profile picture URL cache
 * Useful if profile pictures are updated
 */
export const clearProfilePictureCache = () => {
  profilePictureUrlCache.clear();
};

/**
 * Clear a specific profile picture from cache
 * @param {string} profilePicturePath - Storage path to clear from cache
 */
export const clearProfilePictureFromCache = (profilePicturePath) => {
  profilePictureUrlCache.delete(profilePicturePath);
};

/**
 * Download firmware file (bootloader or partitions) from Firebase Storage
 * @param {string} type - 'bootloader' or 'partitions'
 * @param {string} [variant] - hardware variant (e.g. 'magnetic_25', 'display_v2_26'); backend defaults to magnetic_25
 * @returns {Promise<Uint8Array|null>} File content as Uint8Array or null if error
 */
export const downloadFirmwareFile = async (type, variant) => {
  try {
    const idToken = await getIdToken();
    if (!idToken) {
      console.error('Not authenticated - cannot fetch firmware file');
      return null;
    }

    const query = variant ? `?variant=${encodeURIComponent(variant)}` : '';
    const response = await fetch(buildApiUrl(`/firmware/${type}${query}`), {
      method: 'GET',
      headers: getAuthHeaders(idToken)
    });

    if (!response.ok) {
      if (response.status === 404) {
        console.error(`Firmware file ${type}.bin not found`);
        return null;
      }
      throw new Error(`Failed to get firmware file: ${response.statusText}`);
    }

    const arrayBuffer = await response.arrayBuffer();
    return new Uint8Array(arrayBuffer);
  } catch (error) {
    console.error(`Error downloading firmware file ${type}:`, error);
    return null;
  }
};

/**
 * Get available firmware channels for the current user
 * @returns {Promise<{channels: string[]}|null>} Available channels or null if error
 */
export const getAvailableFirmwareChannels = async () => {
  try {
    const idToken = await getIdToken();
    if (!idToken) {
      console.error('Not authenticated - cannot fetch firmware channels');
      return null;
    }

    const response = await fetch(buildApiUrl('/firmware/channels'), {
      method: 'GET',
      headers: getAuthHeaders(idToken)
    });

    if (!response.ok) {
      throw new Error(`Failed to get firmware channels: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching firmware channels:', error);
    return null;
  }
};

/**
 * Get firmware manifests for a specific channel
 * @param {string} channel - 'public', 'beta', or 'alpha'
 * @param {string} [variant] - hardware variant (e.g. 'magnetic_25', 'display_v2_26'); backend defaults to magnetic_25
 * @returns {Promise<{channel: string, manifests: Array}|null>} Channel data or null if error
 */
export const getFirmwareChannel = async (channel, variant) => {
  try {
    const idToken = await getIdToken();
    if (!idToken) {
      console.error('Not authenticated - cannot fetch firmware channel');
      return null;
    }

    const query = variant ? `?variant=${encodeURIComponent(variant)}` : '';
    const response = await fetch(buildApiUrl(`/firmware/channel/${channel}${query}`), {
      method: 'GET',
      headers: getAuthHeaders(idToken)
    });

    if (!response.ok) {
      if (response.status === 404) {
        console.error(`Firmware channel ${channel} not found`);
        return null;
      }
      throw new Error(`Failed to get firmware channel: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error(`Error fetching firmware channel ${channel}:`, error);
    return null;
  }
};

/**
 * Get channels available for firmware seeding (@rekkie.com users only)
 * @returns {Promise<{channels: string[]}|null>} Seedable channels or null if error/forbidden
 */
export const getSeedableChannels = async () => {
  try {
    const idToken = await getIdToken();
    if (!idToken) return null;

    const response = await fetch(buildApiUrl('/firmware/seedable-channels'), {
      method: 'GET',
      headers: getAuthHeaders(idToken)
    });

    if (!response.ok) {
      if (response.status === 403) return null;
      throw new Error(`Failed to get seedable channels: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching seedable channels:', error);
    return null;
  }
};

/**
 * Seed firmware to a channel (upload .bin + .elf, update manifest)
 * @param {Object} params
 * @param {File} params.firmwareBin - .bin file
 * @param {File} params.firmwareElf - .elf file
 * @param {number} params.major
 * @param {number} params.minor
 * @param {number} params.build
 * @param {number} params.patch
 * @param {string} params.channel
 * @param {string} params.changelog
 * @param {string} params.developerNotes
 * @param {string} [params.variant] - hardware variant (e.g. 'magnetic_25', 'display_v2_26'); backend defaults to magnetic_25
 * @returns {Promise<Object|null>} Seed result or null on error
 */
export const seedFirmware = async ({ firmwareBin, firmwareElf, major, minor, build, patch, channel, changelog, developerNotes, variant }) => {
  try {
    const idToken = await getIdToken();
    if (!idToken) {
      console.error('Not authenticated - cannot seed firmware');
      return null;
    }

    const formData = new FormData();
    formData.append('firmwareBin', firmwareBin);
    formData.append('firmwareElf', firmwareElf);
    formData.append('major', String(major));
    formData.append('minor', String(minor));
    formData.append('build', String(build));
    formData.append('patch', String(patch));
    formData.append('channel', channel);
    formData.append('changelog', changelog);
    formData.append('developerNotes', developerNotes);
    if (variant) formData.append('variant', variant);

    const response = await fetch(buildApiUrl('/firmware/seed'), {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${idToken}`
        // No Content-Type — browser sets multipart boundary automatically
      },
      body: formData
    });

    if (!response.ok) {
      const errData = await response.json().catch(() => ({}));
      throw new Error(errData.message || `Seed failed: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error seeding firmware:', error);
    throw error;
  }
};

/**
 * Download firmware from a Firebase Storage URL
 * @param {string} url - Firebase Storage URL (gs:// or https://)
 * @returns {Promise<Uint8Array|null>} File content as Uint8Array or null if error
 */
export const downloadFirmwareFromUrl = async (url) => {
  try {
    const idToken = await getIdToken();
    if (!idToken) {
      console.error('Not authenticated - cannot download firmware');
      return null;
    }

    const response = await fetch(buildApiUrl('/firmware/download'), {
      method: 'POST',
      headers: getAuthHeaders(idToken),
      body: JSON.stringify({ url })
    });

    if (!response.ok) {
      if (response.status === 404) {
        console.error('Firmware file not found at URL');
        return null;
      }
      throw new Error(`Failed to download firmware: ${response.statusText}`);
    }

    const arrayBuffer = await response.arrayBuffer();
    return new Uint8Array(arrayBuffer);
  } catch (error) {
    console.error('Error downloading firmware from URL:', error);
    return null;
  }
};
